import type { OperationKey } from '@foodlista/operations'
import type { OperationItem } from '../types'
import { readOperationContext } from './read-operation-context'

function isEmptyScopeValue(value: unknown): boolean {
  return value === undefined || value === null || value === ''
}

/**
 * Scope fields come from the immutable operation context, which the envelope
 * carries from the moment it is created — so matching works before a run
 * exists. `readOperationContext` rejects anything this client cannot read
 * (no context, unknown type, other projection version), and such envelopes
 * never rehydrate their scope, by design.
 */
function scopeFieldFromItem(item: OperationItem, scopeKey: string): unknown | null {
  if (scopeKey === 'restaurantSlug') {
    return item.restaurantSlug
  }

  const context = readOperationContext(item)
  if (!context) {
    return null
  }

  const value = context[scopeKey]
  return value === undefined ? null : value
}

/**
 * The `scopeKey` an operation was enqueued with, read from its context.
 * Returns null when the envelope carries no readable context.
 */
export function itemScopeKey(item: OperationItem): string | null {
  const value = scopeFieldFromItem(item, 'scopeKey')
  return typeof value === 'string' ? value : null
}

export function itemMatchesScope(
  item: OperationItem,
  key: OperationKey,
  scope: Record<string, unknown>,
): boolean {
  if (item.type !== key) {
    return false
  }

  for (const [scopeKey, scopeValue] of Object.entries(scope)) {
    const contextValue = scopeFieldFromItem(item, scopeKey)

    if (!isEmptyScopeValue(scopeValue)) {
      if (contextValue === null) {
        return false
      }
      if (contextValue !== scopeValue) {
        return false
      }
      continue
    }

    if (contextValue !== null && !isEmptyScopeValue(contextValue)) {
      return false
    }
  }

  return true
}
